/**
 * HelpDrawer - FAQ / Help center drawer
 *
 * Sliding drawer from the right with searchable FAQ articles
 * organized by category using MUI Accordion components.
 * Includes a floating help button (FAB) for triggering the drawer.
 */

import React, { useState, useMemo } from 'react';
import {
  Drawer,
  Box,
  Typography,
  IconButton,
  TextField,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Button,
  Fab,
  Divider,
  InputAdornment,
} from '@mui/material';
import {
  Close as CloseIcon,
  ExpandMore as ExpandMoreIcon,
  Search as SearchIcon,
  HelpOutline as HelpOutlineIcon,
  WhatsApp as WhatsAppIcon,
} from '@mui/icons-material';

interface FAQItem {
  question: string;
  answer: string;
}

interface FAQSection {
  title: string;
  items: FAQItem[];
}

const FAQ_DATA: FAQSection[] = [
  {
    title: 'Primeiros Passos',
    items: [
      {
        question: 'Como cadastrar meu primeiro paciente?',
        answer:
          'Acesse o menu "Pacientes" na barra lateral e clique em "Novo Paciente". Preencha os dados pessoais, contato e informações clínicas. O cadastro será salvo automaticamente.',
      },
      {
        question: 'Como agendar uma consulta?',
        answer:
          'Vá até a "Agenda" no menu lateral. Clique no horário desejado ou no botão "Nova Consulta". Selecione o paciente, data, horário e local da consulta.',
      },
      {
        question: 'Como registrar uma sessão?',
        answer:
          'Após a consulta, acesse "Sessões" no menu ou vá direto pela ficha do paciente. Clique em "Nova Sessão", preencha as anotações clínicas e salve o registro.',
      },
    ],
  },
  {
    title: 'Agenda',
    items: [
      {
        question: 'Como criar agendamentos recorrentes?',
        answer:
          'Ao criar um novo agendamento, ative a opção "Recorrente". Você pode definir a frequência (semanal, quinzenal, mensal) e a quantidade de repetições desejada.',
      },
      {
        question: 'Como confirmar presença?',
        answer:
          'Na agenda, clique sobre a consulta e selecione "Confirmar Presença". Você também pode enviar lembretes automáticos por WhatsApp ou e-mail para o paciente confirmar.',
      },
      {
        question: 'Como usar drag and drop?',
        answer:
          'Na visualização semanal da agenda, você pode arrastar consultas entre horários para reagendar. Basta clicar e segurar sobre a consulta e soltar no novo horário.',
      },
    ],
  },
  {
    title: 'Prontuário',
    items: [
      {
        question: 'Como usar os templates de sessão?',
        answer:
          'Ao criar uma nova sessão, clique em "Usar Template" para escolher um modelo pré-definido. Você pode personalizar templates na "Biblioteca de Templates" no menu de configurações.',
      },
      {
        question: 'Como aplicar testes (PHQ-9, GAD-7)?',
        answer:
          'Dentro da ficha do paciente, acesse a aba de avaliações. Selecione o teste desejado, preencha as respostas junto com o paciente e o sistema calculará a pontuação automaticamente.',
      },
      {
        question: 'Como gerar um laudo?',
        answer:
          'Acesse "Gerar Documentos" no menu de configurações. Escolha o tipo de documento (laudo, declaração, encaminhamento), selecione o paciente e preencha os campos necessários.',
      },
    ],
  },
  {
    title: 'Financeiro',
    items: [
      {
        question: 'Como registrar pagamentos?',
        answer:
          'Na ficha do paciente, acesse a aba financeira. Clique em "Registrar Pagamento", informe o valor, forma de pagamento e data. O sistema mantém o histórico completo.',
      },
      {
        question: 'Como gerar recibos?',
        answer:
          'Após registrar um pagamento, clique em "Gerar Recibo". O documento será gerado em PDF com todas as informações do profissional, paciente e serviço prestado.',
      },
      {
        question: 'Como controlar inadimplência?',
        answer:
          'O painel financeiro exibe automaticamente pacientes com pagamentos pendentes. Você pode configurar lembretes automáticos de cobrança nas configurações de notificações.',
      },
    ],
  },
  {
    title: 'Configurações',
    items: [
      {
        question: 'Como adicionar membros da equipe?',
        answer:
          'Acesse "Equipe" no menu lateral. Clique em "Convidar Membro", informe o e-mail e a função (psicólogo, secretária, etc.). O convidado receberá um link para criar a conta.',
      },
      {
        question: 'Como personalizar minha clínica?',
        answer:
          'Vá até "Configurações" ou "Personalização" no menu. Você pode alterar o logo, cores, nome da clínica e informações de contato que aparecem nos documentos.',
      },
      {
        question: 'Como exportar meus dados?',
        answer:
          'Acesse "Configurações" e procure a opção de exportação de dados. Você pode exportar pacientes, sessões e agendamentos em formato planilha para backup externo.',
      },
    ],
  },
];

interface HelpDrawerProps {
  open: boolean;
  onClose: () => void;
}

const HelpDrawerContent: React.FC<HelpDrawerProps> = ({ open, onClose }) => {
  const [searchQuery, setSearchQuery] = useState('');

  // Filter FAQ items based on search
  const filteredSections = useMemo(() => {
    if (!searchQuery.trim()) return FAQ_DATA;

    const query = searchQuery.toLowerCase();
    return FAQ_DATA.map((section) => ({
      ...section,
      items: section.items.filter(
        (item) =>
          item.question.toLowerCase().includes(query) ||
          item.answer.toLowerCase().includes(query)
      ),
    })).filter((section) => section.items.length > 0);
  }, [searchQuery]);

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: { width: { xs: '100%', sm: 400 }, maxWidth: '100%' },
      }}
    >
      {/* Header */}
      <Box
        sx={{
          p: 2,
          bgcolor: 'primary.main',
          color: 'white',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Typography variant="h6" fontWeight={600}>
          Central de Ajuda
        </Typography>
        <IconButton onClick={onClose} sx={{ color: 'white' }}>
          <CloseIcon />
        </IconButton>
      </Box>

      {/* Search */}
      <Box sx={{ p: 2 }}>
        <TextField
          fullWidth
          size="small"
          placeholder="Buscar na ajuda..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon color="action" />
              </InputAdornment>
            ),
          }}
        />
      </Box>

      {/* FAQ Sections */}
      <Box sx={{ flex: 1, overflowY: 'auto', px: 1 }}>
        {filteredSections.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="body2" color="text.secondary">
              Nenhum resultado encontrado para "{searchQuery}"
            </Typography>
          </Box>
        ) : (
          filteredSections.map((section) => (
            <Box key={section.title} sx={{ mb: 1 }}>
              <Typography
                variant="overline"
                sx={{
                  px: 2,
                  py: 1,
                  display: 'block',
                  fontWeight: 600,
                  color: 'text.secondary',
                  fontSize: '0.7rem',
                }}
              >
                {section.title}
              </Typography>
              {section.items.map((item, idx) => (
                <Accordion
                  key={idx}
                  disableGutters
                  elevation={0}
                  sx={{
                    '&:before': { display: 'none' },
                    bgcolor: 'transparent',
                  }}
                >
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    sx={{
                      px: 2,
                      minHeight: 'auto',
                      '& .MuiAccordionSummary-content': { my: 1 },
                    }}
                  >
                    <Typography variant="body2" fontWeight={500}>
                      {item.question}
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails sx={{ px: 2, pt: 0, pb: 1.5 }}>
                    <Typography variant="body2" color="text.secondary">
                      {item.answer}
                    </Typography>
                  </AccordionDetails>
                </Accordion>
              ))}
            </Box>
          ))
        )}
      </Box>

      {/* Footer */}
      <Divider />
      <Box sx={{ p: 2, textAlign: 'center' }}>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          Precisa de mais ajuda?
        </Typography>
        <Button
          variant="outlined"
          color="success"
          startIcon={<WhatsAppIcon />}
          href="https://wa.me/5500000000000"
          target="_blank"
          rel="noopener noreferrer"
          size="small"
        >
          Falar pelo WhatsApp
        </Button>
      </Box>
    </Drawer>
  );
};

/**
 * HelpFAB - Floating action button that opens the HelpDrawer.
 * Should be placed in the Layout for authenticated pages.
 */
export const HelpFAB: React.FC<{ onClick: () => void }> = ({ onClick }) => {
  return (
    <Fab
      color="primary"
      aria-label="Ajuda"
      onClick={onClick}
      sx={{
        position: 'fixed',
        bottom: 24,
        right: 24,
        zIndex: 1200,
      }}
    >
      <HelpOutlineIcon />
    </Fab>
  );
};

export default HelpDrawerContent;
